import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import { Loader } from '@components';
import GlobalStyles from '../../../styles';
import ThemeProvider from '../../../themes/internal';
import { SET_CURRENT_MANAGER } from '../../../store/actions/general';
import { setAgentSession } from '../../../store/reducers/agentAuth';
import { isAgentModeSelector } from '../../../store/selectors/appMode';

import AuthPage from './AuthPage';
import { AgentCredentials } from '../api';
import { AGENT_ORDERS_ROUTE } from '../orders';
import { AGENT_AUTH_ROUTE } from './config';
import {
  AgentApiError,
  AgentSession,
  getAgentSession,
  loginAgent,
  loginAgentWithStaffCode,
} from './api';

interface AuthGateProps {
  children: React.ReactNode;
}

const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isAgentMode = useSelector(isAgentModeSelector);
  const [authStatus, setAuthStatus] = useState<
    'checking' | 'authenticated' | 'anonymous'
  >('checking');
  const [initialError, setInitialError] = useState('');

  const applySession = useCallback(
    (session: AgentSession) => {
      const user = session.agency_user || session.staff_user;

      dispatch(setAgentSession(session));
      dispatch({
        payload: {
          currentManager: {
            name: user?.name || user?.email || session.agency.name,
            id: String(user?.id || session.agency.id),
            number: null,
            avatarUrl: null,
            isOperator: false,
            isSupervisor: session.role === 'supervisor',
            operatorState: 1,
            manualState: 1,
            remoteState: null,
            skillGroupIds: [],
            activeTicketsCount: 0,
            reservedTicketsCount: 0,
            isAlfaTravelManager: false,
          },
        },
        type: SET_CURRENT_MANAGER,
      });
      setAuthStatus('authenticated');
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isAgentMode) return undefined;

    let isCancelled = false;

    const restoreSession = async () => {
      const hash = new URLSearchParams(window.location.hash.slice(1));
      const staffCode = hash.get('staff_code');

      if (staffCode) {
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${window.location.search}`
        );
      }

      try {
        const session = staffCode
          ? await loginAgentWithStaffCode(staffCode)
          : await getAgentSession();

        if (!isCancelled) applySession(session);
      } catch (error) {
        if (isCancelled) return;

        setAuthStatus('anonymous');
        if (staffCode) {
          setInitialError(
            error instanceof AgentApiError && error.status === 401
              ? 'Ссылка для входа недействительна или уже использована'
              : 'Не удалось войти по ссылке сотрудника'
          );
        }
      }
    };

    restoreSession();

    return () => {
      isCancelled = true;
    };
  }, [applySession, isAgentMode]);

  useEffect(() => {
    if (!isAgentMode) return;

    if (authStatus === 'anonymous' && location.pathname !== AGENT_AUTH_ROUTE) {
      history.replace(AGENT_AUTH_ROUTE);
      return;
    }

    if (
      authStatus === 'authenticated' &&
      location.pathname === AGENT_AUTH_ROUTE
    ) {
      history.replace(AGENT_ORDERS_ROUTE);
    }
  }, [authStatus, history, isAgentMode, location.pathname]);

  if (!isAgentMode) {
    return <>{children}</>;
  }

  if (authStatus === 'authenticated') {
    return (
      <>
        <AgentGlobalStyles />
        {children}
      </>
    );
  }

  const handleLogin = async (credentials: AgentCredentials) => {
    const session = await loginAgent(credentials);
    applySession(session);
    history.replace(AGENT_ORDERS_ROUTE);
  };

  return (
    <ThemeProvider>
      <GlobalStyles theme="light" />
      <AgentGlobalStyles />
      {authStatus === 'checking' ? (
        <CheckingPage>
          <Loader />
        </CheckingPage>
      ) : (
        <AuthPage initialError={initialError} onSubmit={handleLogin} />
      )}
    </ThemeProvider>
  );
};

const CheckingPage = styled.div`
  align-items: center;
  background: #f3f7fb;
  display: flex;
  justify-content: center;
  min-height: 100vh;
`;

// Кнопки без своего фона (Button variant="default"/"error") в агентских страницах — прозрачные, а не серые браузерные.
// Селектор по тегу слабее классов styled-components: заданный фон (primary, строки списков) остаётся.
const AgentGlobalStyles = createGlobalStyle`
  button {
    background-color: transparent;
  }
`;

export default AuthGate;
